import type React from 'react'
import { useMemo, useState } from 'react'
import { createColumns } from '../core/filters'
import { DEFAULT_OPERATORS, determineNewOperator } from '../core/operators'
import type {
  ColumnConfig,
  ColumnDataType,
  ColumnOption,
  DataTableFilterActions,
  FilterModel,
  FilterStrategy,
  FiltersState,
  NumberColumnIds,
  OptionBasedColumnDataType,
  OptionColumnIds,
} from '../core/types'
import { addUniq, removeUniq, uniq } from '../lib/array'
import {
  createDateFilterValue,
  createDurationRangeFilterValue,
  createNumberFilterValue,
  isColumnOptionArray,
  isColumnOptionMap,
  isMinMaxTuple,
} from '../lib/helpers'

export interface DataTableFiltersOptions<
  TData,
  // biome-ignore lint/suspicious/noExplicitAny: Generic type constraint requires any
  TColumns extends ReadonlyArray<ColumnConfig<TData, any, any, any>>,
  TStrategy extends FilterStrategy,
> {
  strategy: TStrategy
  data: TData[]
  columnsConfig: TColumns
  defaultFilters?: FiltersState
  filters?: FiltersState
  onFiltersChange?: React.Dispatch<React.SetStateAction<FiltersState>>
  // biome-ignore lint/suspicious/noExplicitAny: Type variance issue with column IDs
  options?: Partial<Record<OptionColumnIds<any>, ColumnOption[] | undefined>>
  faceted?: Partial<
    // biome-ignore lint/suspicious/noExplicitAny: Type variance issue with column IDs
    | Record<OptionColumnIds<any>, Map<string, number> | undefined>
    // biome-ignore lint/suspicious/noExplicitAny: Type variance issue with column IDs
    | Record<NumberColumnIds<any>, [number, number] | undefined>
  >
}

export function useDataTableFilters<
  TData,
  // biome-ignore lint/suspicious/noExplicitAny: Generic type constraint requires any
  TColumns extends ReadonlyArray<ColumnConfig<TData, any, any, any>>,
  TStrategy extends FilterStrategy,
>({
  strategy,
  data,
  columnsConfig,
  defaultFilters,
  filters: externalFilters,
  onFiltersChange,
  options,
  faceted,
}: DataTableFiltersOptions<TData, TColumns, TStrategy>) {
  const [internalFilters, setInternalFilters] = useState<FiltersState>(defaultFilters ?? [])

  if ((externalFilters && !onFiltersChange) || (!externalFilters && onFiltersChange)) {
    throw new Error('If using controlled state, you must specify both filters and onFiltersChange.')
  }

  const filters = externalFilters ?? internalFilters
  const setFilters = onFiltersChange ?? setInternalFilters

  // Convert ColumnConfig to Column, applying options and faceted options if provided
  const columns = useMemo(() => {
    const enhancedConfigs = columnsConfig.map((config) => {
      let final = config

      if (options && (config.type === 'option' || config.type === 'multiOption')) {
        const optionsInput = options[config.id as string]
        if (!optionsInput || !isColumnOptionArray(optionsInput)) return config

        final = { ...final, options: optionsInput }
      }

      if (faceted && (config.type === 'option' || config.type === 'multiOption')) {
        const facetedOptionsInput = faceted[config.id as string]
        if (!facetedOptionsInput || !isColumnOptionMap(facetedOptionsInput)) return config

        final = { ...final, facetedOptions: facetedOptionsInput }
      }

      if (config.type === 'number' && faceted) {
        const minMaxTuple = faceted[config.id as string]
        if (!minMaxTuple || !isMinMaxTuple(minMaxTuple)) return config

        final = {
          ...final,
          min: minMaxTuple[0],
          max: minMaxTuple[1],
        }
      }

      return final
    })

    return createColumns(data, enhancedConfigs, strategy)
  }, [data, columnsConfig, options, faceted, strategy])

  const actions: DataTableFilterActions = useMemo(
    () => ({
      addFilterValue<TData, TType extends OptionBasedColumnDataType>(
        column: ColumnConfig<TData, TType>,
        values: FilterModel<TType>['values'],
      ) {
        if (column.type !== 'option' && column.type !== 'multiOption') {
          throw new Error(
            '[data-table-filter] addFilterValue() is only supported for option columns',
          )
        }

        setFilters((prev) => {
          const filter = prev.find((f) => f.columnId === column.id)
          const isColumnFiltered = filter && filter.values.length > 0

          if (!isColumnFiltered) {
            return [
              ...prev,
              {
                columnId: column.id,
                type: column.type,
                operator:
                  values.length > 1
                    ? DEFAULT_OPERATORS[column.type].multiple
                    : DEFAULT_OPERATORS[column.type].single,
                values,
              },
            ]
          }

          const oldValues = filter.values
          const newValues = column.type === 'multiOption' ? addUniq(filter.values, values) : values
          const newOperator = determineNewOperator(
            column.type,
            oldValues,
            newValues,
            filter.operator,
          )

          if (newValues.length === 0) {
            return prev.filter((f) => f.columnId !== column.id)
          }

          return prev.map((f) =>
            f.columnId === column.id
              ? {
                  columnId: column.id,
                  type: column.type,
                  operator: newOperator,
                  values: newValues,
                }
              : f,
          )
        })
      },
      removeFilterValue<TData, TType extends OptionBasedColumnDataType>(
        column: ColumnConfig<TData, TType>,
        value: FilterModel<TType>['values'],
      ) {
        if (column.type !== 'option' && column.type !== 'multiOption') {
          throw new Error(
            '[data-table-filter] removeFilterValue() is only supported for option columns',
          )
        }

        setFilters((prev) => {
          const filter = prev.find((f) => f.columnId === column.id)
          const isColumnFiltered = filter && filter.values.length > 0

          if (!isColumnFiltered) {
            return prev
          }

          const newValues = removeUniq(filter.values, value)
          const oldValues = filter.values
          const newOperator = determineNewOperator(
            column.type,
            oldValues,
            newValues,
            filter.operator,
          )

          if (newValues.length === 0) {
            return prev.filter((f) => f.columnId !== column.id)
          }

          return prev.map((f) =>
            f.columnId === column.id
              ? {
                  columnId: column.id,
                  type: column.type,
                  operator: newOperator,
                  values: newValues,
                }
              : f,
          )
        })
      },
      setFilterValue<TData, TType extends ColumnDataType>(
        column: ColumnConfig<TData, TType>,
        values: FilterModel<TType>['values'],
      ) {
        setFilters((prev) => {
          const filter = prev.find((f) => f.columnId === column.id)
          const isColumnFiltered = filter && filter.values.length > 0
          const newValues =
            column.type === 'number'
              ? createNumberFilterValue(values as number[])
              : column.type === 'date'
                ? createDateFilterValue(values as [Date, Date] | [Date] | [] | undefined)
                : column.type === 'durationRange'
                  ? createDurationRangeFilterValue(values as number[])
                  : uniq(values)
          if (newValues.length === 0) return prev
          if (!isColumnFiltered) {
            return [
              ...prev,
              {
                columnId: column.id,
                type: column.type,
                operator:
                  values.length > 1
                    ? DEFAULT_OPERATORS[column.type].multiple
                    : DEFAULT_OPERATORS[column.type].single,
                values: newValues,
              },
            ]
          }
          const oldValues = filter.values
          const newOperator = determineNewOperator(
            column.type,
            oldValues,
            newValues,
            filter.operator,
          )
          const newFilter = {
            columnId: column.id,
            type: column.type,
            operator: newOperator,
            values: newValues,
          } as FilterModel<TType>
          return prev.map((f) => (f.columnId === column.id ? newFilter : f))
        })
      },
      setFilterOperator<TType extends ColumnDataType>(
        columnId: string,
        operator: FilterModel<TType>['operator'],
      ) {
        setFilters((prev) => prev.map((f) => (f.columnId === columnId ? { ...f, operator } : f)))
      },
      removeFilter(columnId: string) {
        setFilters((prev) => prev.filter((f) => f.columnId !== columnId))
      },
      removeAllFilters() {
        setFilters([])
      },
    }),
    [setFilters],
  )

  return { columns, filters, actions, strategy } // columns is Column<TData>[]
}
